import React, { useMemo } from 'react';
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTheme } from '@mui/material/styles';
import { format } from 'date-fns';

const HedgePerformanceComboChart = ({ data }) => {
  const theme = useTheme();
  
  // Process the data for the chart
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Group by month and energy source
    const groupedData = data.reduce((acc, item) => {
      const month = item.month;
      if (!acc[month]) {
        acc[month] = {
          month,
          strike_price: 0,
          actual_price: 0,
          payout: 0,
          count: 0
        };
      }
      
      // Calculate weighted averages
      acc[month].strike_price = (acc[month].strike_price * acc[month].count + item.avg_strike_price) / (acc[month].count + 1);
      acc[month].actual_price = (acc[month].actual_price * acc[month].count + item.avg_actual_price) / (acc[month].count + 1);
      acc[month].payout += item.total_payout;
      acc[month].count += 1;
      
      return acc;
    }, {});
    
    // Convert to array and sort by month
    return Object.values(groupedData)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map(item => ({
        month: item.month,
        strike_price: item.strike_price,
        actual_price: item.actual_price,
        payout: item.payout
      }));
  }, [data]);
  
  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ 
          backgroundColor: '#fff', 
          padding: '10px', 
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{formatMonth(label)}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '5px 0', color: entry.color }}>
              {entry.name}: {entry.dataKey === 'payout' ? 
                `$${entry.value.toLocaleString()}` : 
                `$${entry.value.toFixed(2)}/MWh`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };
  
  // Format month for display
  const formatMonth = (month) => {
    const [year, monthNum] = month.split('-');
    return format(new Date(parseInt(year), parseInt(monthNum) - 1), 'MMM yyyy');
  };

  return (
    <ResponsiveContainer width="100%" height={400}>
      <ComposedChart
        data={chartData}
        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey="month" 
          tickFormatter={formatMonth}
          angle={-45}
          textAnchor="end" 
          height={80}
        />
        <YAxis 
          yAxisId="left"
          tickFormatter={(value) => `$${value.toFixed(0)}`}
          label={{ value: 'Price ($/MWh)', angle: -90, position: 'insideLeft' }}
        />
        <YAxis 
          yAxisId="right"
          orientation="right"
          tickFormatter={(value) => `$${value / 1000}k`}
          label={{ value: 'Payout ($)', angle: 90, position: 'insideRight' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Line 
          yAxisId="left"
          type="monotone" 
          dataKey="strike_price" 
          name="Strike Price" 
          stroke="#8884d8" 
          activeDot={{ r: 8 }}
          strokeWidth={2}
        />
        <Line 
          yAxisId="left"
          type="monotone" 
          dataKey="actual_price" 
          name="Actual Price" 
          stroke="#82ca9d" 
          activeDot={{ r: 8 }}
          strokeWidth={2}
        />
        <Bar 
          yAxisId="right"
          dataKey="payout" 
          name="Settlement Payout" 
          fill="#ffc658" 
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default HedgePerformanceComboChart;
